import React from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import makeStyles from '@mui/styles/makeStyles';
import Divider from './Divider';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';

const CofResultDetail = ({ rule: { business_rules_evaluation_result, cof_result }, summary }) => {
    const {
        status: cof_status,
        comments: cof_comments,
        cof_evaluation_status,
        client_specific_cof_status: cof_client_specific_cof_status,
    } = cof_result || {};
    const classes = useStyles();
    if (!cof_result) return null;
    const fitMapping = getFitCategory(cof_status?.name);
    const statusColorMapping = COFStatusColorMapping[fitMapping];
    // The backend returns {} rather than [] when there are no rule results, so check for an array
    const summaryItems = Array.isArray(summary) ? summary : [];
    return (
        <>
            <div
                className={classes.mainHeader}
                style={{
                    borderLeftColor: statusColorMapping.accent,
                    backgroundColor: statusColorMapping.backdrop,
                }}
            >
                <Typography
                    style={{ textTransform: 'capitalize', fontWeight: 'bold', fontSize: '14px' }}
                >
                    {cof_evaluation_status} Certificate of Fitness Result
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight="bold"
                    style={{ color: statusColorMapping.maintext, fontSize: '16px' }}
                >
                    {cof_client_specific_cof_status}
                </Typography>
            </div>
            {summaryItems.length > 0 ? (
                <div className={classes.root}>
                    {summaryItems.map((summary_item, idx) => {
                        const comment_results = summary_item?.comment_results ?? [];
                        const fitMapping = getFitCategory(summary_item?.status?.name);
                        const statusColorMapping = COFStatusColorMapping[fitMapping];
                        return (
                            <Accordion
                                key={idx}
                                square={true}
                                className={classes.accordianContainer}
                                style={{ borderLeftColor: statusColorMapping.accent }}
                            >
                                <AccordionSummary
                                    sx={{
                                        minHeight: 35, // Reduces the overall height of the row
                                        px: 1, // Reduces horizontal padding
                                        '&.Mui-expanded': {
                                            minHeight: 35, // Prevents snapping to a larger height when open
                                        },
                                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                                            {
                                                my: 0.5, // Keeps it compact, collapsed or expanded
                                            },
                                    }}
                                    expandIcon={<ExpandMoreIcon style={{ color: '#D3D3D3' }} />}
                                >
                                    <ProcedureHeader
                                        procedure_name={summary_item?.procedure_name}
                                        status={summary_item?.status?.name}
                                        num_comments={comment_results.length}
                                    />
                                </AccordionSummary>
                                <AccordionDetails className={classes.accordianDetailsContainer}>
                                    {comment_results.map((comment_result, child_idx) => {
                                        return (
                                            <CommentResult
                                                key={`${idx}#${child_idx}`}
                                                comment={comment_result?.comment}
                                                status={comment_result?.status?.name}
                                            />
                                        );
                                    })}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </div>
            ) : (
                // Fallback for consults without a rules summary (e.g. historical consults)
                cof_comments.length > 0 && (
                    <div className={classes.root}>
                        <Typography variant="body1">Comments:</Typography>
                        <ul>
                            {cof_comments.map((note, idx) => (
                                <li key={idx}>
                                    <p>{note}</p>
                                </li>
                            ))}
                        </ul>
                    </div>
                )
            )}
        </>
    );
};

const CoMorbidityResultDetail = ({
    rule: { business_rules_evaluation_result, co_morbidity_result },
}) => {
    const {
        status: co_morbidity_status,
        comments: co_morbidity_comments,
        other_comments_and_statuses: co_morbidity_other_comments,
        co_morbidity_evaluation_status,
    } = co_morbidity_result || {};
    return (
        co_morbidity_result && (
            <div>
                <Typography
                    style={{ textTransform: 'capitalize', fontWeight: 'bold', fontSize: '14px' }}
                >
                    {co_morbidity_evaluation_status} Co-morbidity Risk Stratification
                </Typography>
                <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                    style={{ fontSize: '16px' }}
                >
                    {co_morbidity_status.name}
                </Typography>
                {co_morbidity_comments && co_morbidity_comments.length >= 1 && (
                    <Typography variant="body1">Comments:</Typography>
                )}
                {co_morbidity_comments && co_morbidity_comments.length >= 1 && (
                    <ul>
                        {co_morbidity_comments.map((note, idx) => (
                            <li key={idx}>
                                <p>{note}</p>
                            </li>
                        ))}
                    </ul>
                )}
                {co_morbidity_other_comments && co_morbidity_other_comments.length >= 1 && (
                    <>
                        <Divider />
                        <Typography variant="body1">Other co-morbidity risk comments:</Typography>
                    </>
                )}
                {co_morbidity_other_comments && co_morbidity_other_comments.length >= 1 && (
                    <ul>
                        {co_morbidity_other_comments.map((comment_and_status, idx) => (
                            <li key={idx}>
                                <p>
                                    {comment_and_status.comments} [{comment_and_status.status.name}]
                                </p>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        )
    );
};

const ConsultResult = ({ props: { rules_result, consult_cof_result_summary } }) => {
    const classes = useStyles();
    if (!rules_result?.cof_result && !rules_result?.co_morbidity_result) return null;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '24px' }}>
            <Paper>
                <CofResultDetail rule={rules_result} summary={consult_cof_result_summary} />
            </Paper>
            <Paper className={classes.root}>
                <CoMorbidityResultDetail rule={rules_result} />
            </Paper>
        </div>
    );
};

export default ConsultResult;

const ProcedureHeader = ({ procedure_name, num_comments, status }) => {
    const classes = useStyles();
    return (
        <div className={classes.procedureHeader}>
            <div style={{ display: 'inline-flex', gap: '10px', alignItems: 'center' }}>
                <div
                    style={{
                        fontWeight: 'bold',
                        fontSize: '13.5px',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {procedure_name}
                </div>
                <div
                    style={{
                        color: '#8B979C',
                        fontSize: '12px',
                        width: 'fit-content',
                        whiteSpace: 'nowrap',
                    }}
                >
                    • {num_comments} {num_comments === 1 ? 'comment' : 'comments'}
                </div>
            </div>
            <StatusBadge status={status} />
        </div>
    );
};

const CommentResult = ({ comment, status }) => {
    const classes = useStyles();
    const fitMapping = getFitCategory(status);
    const statusColorMapping = COFStatusColorMapping[fitMapping];
    return (
        <div className={classes.commentLine} style={{ borderLeftColor: statusColorMapping.accent }}>
            <div>{comment}</div>
            <StatusBadge status={status} />
        </div>
    );
};

const StatusBadge = ({ status }) => {
    const classes = useStyles();
    const fitMapping = getFitCategory(status);
    const statusColorMapping = COFStatusColorMapping[fitMapping];
    return (
        <div
            className={classes.statusBadge}
            style={{
                backgroundColor: statusColorMapping.background,
                color: statusColorMapping.text,
                borderColor: statusColorMapping.text,
            }}
        >
            {status}
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        padding: '15px 10px',
    },
    mainHeader: {
        backgroundColor: 'pink',
        borderTopLeftRadius: '5px',
        borderTopRightRadius: '5px',
        borderLeftStyle: 'solid',
        borderLeftWidth: '5px',
        paddingBottom: '5px',
        paddingTop: '5px',
        paddingLeft: 16,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    accordianContainer: {
        marginBottom: '8px',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: 'rgb(212 212 212 / 0.5)',
        borderRadius: '5px',
        boxShadow: 'none',
        textShadow: 'none',
        '&::before': {
            display: 'none',
        },
        borderLeftStyle: 'solid',
        borderLeftWidth: '5px',
    },
    accordianDetailsContainer: {
        display: 'flex',
        flexDirection: 'column',
    },
    procedureHeader: {
        width: '100%',
        display: 'flex',
        border: 'none',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 0,
    },
    commentLine: {
        fontSize: '13px',
        fontWeight: '400',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderStyle: 'solid',
        borderWidth: '1px',
        borderColor: 'rgb(212 212 212 / 0.5)',
        borderRadius: '5px',
        padding: '5px 10px',
        marginBottom: '8px',
        borderLeftStyle: 'solid',
        borderLeftWidth: '3px',
    },
    statusBadge: {
        display: 'inline-block',
        justifyContent: 'center',
        borderRadius: '15px',
        borderWidth: '1px',
        borderStyle: 'solid',
        width: 'fit-content',
        height: 'fit-content',
        fontSize: '11px',
        fontWeight: 'bold',
        textAlign: 'center',
        alignItems: 'center',
        padding: '10px',
        paddingBottom: '5px',
        paddingTop: '5px',
        marginInline: '5px',
        whiteSpace: 'nowrap',
    },
}));

const getFitCategory = (fitness_status) => {
    if (
        ['Temporarily unfit', 'Unfit for Working at Heights', 'Unfit for duty'].includes(
            fitness_status
        )
    ) {
        return 'Unfit';
    }
    if (['Fit for duty with follow-up', 'Fit for duty with conditions'].includes(fitness_status)) {
        return 'Conditional Fit';
    }
    if (['Fit for duty'].includes(fitness_status)) {
        return 'Fit';
    }
    // Neutral styling for statuses not listed above, so an unknown status is not shown as unfit
    return 'Unknown';
};

const COFStatusColorMapping = {
    Unfit: {
        backdrop: '#f9ebed',
        accent: '#d0989f',
        background: '#9b3a47',
        maintext: '#9b3a47',
        text: 'white',
    },
    'Conditional Fit': {
        backdrop: '#fbf4e6',
        accent: '#e9c897',
        background: '#fbf4e6',
        maintext: '#8a6420',
        text: '#8a6420',
    },
    Fit: {
        backdrop: '#ebf7f3',
        accent: 'rgb(108, 170, 156)',
        background: '#228468',
        maintext: '#228468',
        text: 'white',
    },
    Unknown: {
        backdrop: '#f2f3f4',
        accent: '#b0b7bb',
        background: '#f2f3f4',
        maintext: '#5f6b70',
        text: '#5f6b70',
    },
};
